import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ServiceItem } from "../../../mocks/services";

interface ServiceCardProps {
  service: ServiceItem;
  delay?: number;
  visible?: boolean;
}

export default function ServiceCard({ service, delay = 0, visible = true }: ServiceCardProps) {
  const { t } = useTranslation("services");
  const [expanded, setExpanded] = useState(false);

  // 実 URL が無い場合（空 / "#" などのダミー）はリンクを出さない。
  const linkRaw = (service.link ?? "").trim();
  const hasRealLink = linkRaw.length > 0 && linkRaw !== "#";

  return (
    <article
      style={{
        borderRadius: 20,
        background: "#ffffff",
        border: "1px solid rgba(44,95,79,0.1)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms, box-shadow 0.2s`,
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow =
          "0 8px 32px rgba(44,95,79,0.12)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.boxShadow = "none";
      }}
    >
      {/* Image */}
      <div style={{ position: "relative", height: 220, overflow: "hidden" }}>
        <img
          src={service.imageUrl}
          alt={service.name}
          className="w-full h-full object-cover object-top"
          style={{
            transition: "transform 0.4s ease",
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLImageElement).style.transform = "scale(1.04)")
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLImageElement).style.transform = "scale(1)")
          }
        />
        {/* Overlay on expand */}
        {expanded && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(44,95,79,0.88)",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              overflowY: "auto",
            }}
          >
            <p
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: 14,
                lineHeight: 1.85,
                color: "rgba(255,255,255,0.92)",
              }}
            >
              {service.detail}
            </p>
            {hasRealLink && (
              <a
                href={linkRaw}
                rel="nofollow noopener noreferrer"
                target="_blank"
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
                style={{
                  alignSelf: "flex-start",
                  padding: "7px 16px",
                  border: "1.5px solid rgba(255,255,255,0.7)",
                  borderRadius: 8,
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#ffffff",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLAnchorElement).style.background =
                    "rgba(255,255,255,0.15)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLAnchorElement).style.background = "transparent")
                }
              >
                {t("card.openLink")}
                <i className="ri-external-link-line" style={{ fontSize: 13 }} />
              </a>
            )}
          </div>
        )}
      </div>

      {/* Card Body */}
      <div style={{ padding: "20px 24px 24px", flex: 1, display: "flex", flexDirection: "column" }}>
        <h3
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            fontWeight: 700,
            fontSize: 17,
            color: "#1A1A1A",
            marginBottom: 8,
          }}
        >
          {service.name}
        </h3>
        <p
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            fontSize: 13,
            color: "#888888",
            lineHeight: 1.6,
            flex: 1,
            marginBottom: 16,
          }}
        >
          {service.description}
        </p>

        {/* Toggle Button */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="whitespace-nowrap cursor-pointer inline-flex items-center gap-1 self-start"
          style={{
            fontFamily: "'Noto Sans JP', sans-serif",
            fontWeight: 600,
            fontSize: 13,
            color: "#2C5F4F",
            background: "none",
            border: "none",
            padding: 0,
            transition: "opacity 0.2s",
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.opacity = "0.7")
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.opacity = "1")
          }
        >
          {expanded ? t("card.close") : t("card.showDetail")}
          <i
            className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-right-s-line"}
            style={{ fontSize: 16 }}
          />
        </button>
      </div>
    </article>
  );
}
